<script setup lang="ts">
import type {
  ColDef,
  ICellRendererParams,
  RowSelectionOptions,
} from "ag-grid-community";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import AppGrid from "../../components/AppGrid.vue";
import { DETAIL_GRID_DEFAULT_COL_DEF } from "../../components/composables/useGridColumnDefaults.ts";
import type { IntermediateOrderSkuRow } from "../../redistribution/models/IntermediateOrderSkuRow.ts";
import type { Sku } from "../../redistribution/models/Sku.js";
import { useAppStore } from "../../stores/app.ts";
import {
  computeDaysToStockout,
  getDailyDemandForRateOfSale,
  stockoutBarColor,
  stockoutBarWidth,
} from "../results/resultHelpers.ts";
import {
  DETAIL_GRID_ROW_HEIGHT,
  MAX_DETAIL_VISIBLE_ROWS,
} from "./intermediateOrderGridConfig.ts";

// Full-width row rendered under an expanded Intermediate Order, listing that
// order's SKUs. Mirrors PoDetailRenderer's role on PO Recommendations, column
// for column (minus Urgency, per FEATURE-REQUEST-29 — IO rows have no
// per-SKU threshold to size an urgency call against).
const properties = defineProps<{
  params: ICellRendererParams;
}>();

const appStore = useAppStore();
const { rateOfSale } = storeToRefs(appStore);

interface ItemRow {
  sku: string;
  collection: string;
  description: string;
  color: string;
  qtyOnPo: number;
  cubes: number;
  currentStock: number;
  forecastQuantityAvailable: number;
  dailyDemand: number;
  daysToStockout: number;
  weeksOfStock: number;
}

// Route ROS through the shared resolver so any selected window (not just
// 30/60/90) resolves to a real or estimated daily demand, same as
// PoDetailRenderer. IO rows carry no `dailyRateOfSale` fallback from the API
// (PO Recommendations rows don't either — see Sku mapping), so it's fixed at 0.
function getDailyDemand(row: IntermediateOrderSkuRow): number {
  const rateOfSaleSource: Pick<
    Sku,
    "roS30" | "roS60" | "roS90" | "dailyRateOfSale"
  > = {
    roS30: row.roS30,
    roS60: row.roS60,
    roS90: row.roS90,
    dailyRateOfSale: 0,
  };
  return getDailyDemandForRateOfSale(rateOfSaleSource as Sku, rateOfSale.value);
}

const items = computed<ItemRow[]>(() => {
  const data = properties.params.data as
    | { skus?: IntermediateOrderSkuRow[] }
    | undefined;
  if (!Array.isArray(data?.skus)) {
    return [];
  }

  return data.skus.map((row) => {
    const dailyDemand = getDailyDemand(row);
    const daysToStockout = computeDaysToStockout(
      row.forecastQuantityAvailable,
      dailyDemand
    );
    return {
      sku: row.skuNumber,
      collection: row.collection,
      description: row.productDescription,
      color: row.color,
      qtyOnPo: row.totalUnits,
      cubes: row.totalCubicFeet,
      currentStock: row.currentStock,
      forecastQuantityAvailable: row.forecastQuantityAvailable,
      dailyDemand,
      daysToStockout,
      weeksOfStock: Number((daysToStockout / 7).toFixed(1)),
    };
  });
});

const detailGridId = computed(() => {
  const data = properties.params.data as
    | { intermediatePoNumber?: string }
    | undefined;
  const suffix =
    data?.intermediatePoNumber?.replaceAll(/[^a-zA-Z0-9_-]/g, "-") ?? "items";
  return `intermediate-order-detail-grid-${suffix}`;
});

const detailCellStyle = {
  paddingLeft: "12px",
  paddingRight: "12px",
  fontSize: "12px",
};

function renderWeeksOfStockCell(parameters: {
  data?: ItemRow;
}): HTMLElement | string {
  if (!parameters.data) {
    return "";
  }

  const wrapper = document.createElement("div");
  wrapper.className = "flex items-center gap-2";

  const track = document.createElement("span");
  track.className = "po-detail-renderer-stockout-track";

  const fill = document.createElement("div");
  fill.className = "po-detail-renderer-stockout-fill";
  fill.style.width = stockoutBarWidth(parameters.data.daysToStockout);
  fill.style.background = stockoutBarColor(parameters.data.daysToStockout);

  track.append(fill);
  wrapper.append(track);

  const label = document.createElement("span");
  label.textContent = `${parameters.data.weeksOfStock.toFixed(1)} weeks`;
  wrapper.append(label);

  return wrapper;
}

const columnDefs: ColDef<ItemRow>[] = [
  {
    headerName: "SKU",
    field: "sku",
    minWidth: 70,
    cellStyle: { ...detailCellStyle, fontWeight: "700" },
  },
  { headerName: "Collection", field: "collection", minWidth: 72 },
  { headerName: "Description", field: "description", minWidth: 120, flex: 2 },
  { headerName: "Color", field: "color", minWidth: 65 },
  { headerName: "Qty on PO", field: "qtyOnPo", minWidth: 65 },
  {
    headerName: "Cubes",
    field: "cubes",
    minWidth: 65,
    valueFormatter: (parameters) => {
      const value = Number(parameters.value);
      return Number.isFinite(value) ? value.toFixed(2) : "";
    },
  },
  { headerName: "Current Stock", field: "currentStock", minWidth: 72 },
  {
    headerName: "Forecast QTY",
    field: "forecastQuantityAvailable",
    minWidth: 72,
  },
  {
    headerName: "Daily Demand",
    field: "dailyDemand",
    minWidth: 72,
    valueFormatter: (parameters) => {
      const value = parameters.value as unknown;
      if (typeof value !== "number" || !Number.isFinite(value)) return "—";
      return `${value.toFixed(2)}/day`;
    },
  },
  {
    headerName: "Weeks of Stock",
    field: "weeksOfStock",
    minWidth: 95,
    cellRenderer: renderWeeksOfStockCell,
  },
];

const defaultColDef: ColDef = {
  ...DETAIL_GRID_DEFAULT_COL_DEF,
  cellStyle: detailCellStyle,
};

function getDetailRowHeight(): number {
  return DETAIL_GRID_ROW_HEIGHT;
}

// Detail rows are read-only; selection lives on the parent order rows.
const rowSelection: RowSelectionOptions = {
  mode: "singleRow",
  checkboxes: false,
  enableClickSelection: false,
};
</script>

<template>
  <div class="po-detail-renderer-grid min-w-0 overflow-hidden bg-slate-50 p-0">
    <AppGrid
      :id="detailGridId"
      :column-defs="columnDefs"
      :row-data="items"
      :default-col-def="defaultColDef"
      :row-selection="rowSelection"
      dom-layout="normal"
      :get-row-height="getDetailRowHeight"
      :default-row-height="DETAIL_GRID_ROW_HEIGHT"
      :max-rows="MAX_DETAIL_VISIBLE_ROWS"
      :min-grid-height="0"
    />
  </div>
</template>
